import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Flame, Droplet, Wind, Hammer, ShieldCheck, Award } from 'lucide-react';
import { fetchBySlug, fetchCollection } from '../lib/api';
import GalleryLightbox from '../components/GalleryLightbox';

export default function Services() {
  const [pageData, setPageData] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    async function loadData() {
      const data = await fetchBySlug('da', 'kompetencer');
      setPageData(data);

      const cols = await fetchCollection('galleri');
      // Filter galleries linked to this service page
      const linked = (cols || []).filter(g => g.service_slug === 'kompetencer');
      setGalleries(linked);
    }
    loadData();
  }, []);

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (!pageData) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <div className="animate-pulse text-sm font-semibold tracking-widest uppercase">Henter indhold...</div>
      </div>
    );
  }

  // Verbatim list of competencies from scrape
  const competencies = [
    'Varmepumpe', 'VVS', 'Solceller', 'Ventilation', 'Pillefyr',
    'Naturgas', 'Tagrender', 'Jordvarme', 'Gulvvarme', 'Fjernvarme',
    'Genvex', 'Stokerfyr', 'Solvarme', 'Vandlås', 'Nyt badeværelse',
    'Nybyggeri', 'Blikkenslager', 'Renovering', 'Sanitet'
  ];

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title || 'Ydelser – Møldrup VVS A/S'}</title>
        <meta name="description" content={pageData.meta_description || ''} />
        <html lang="da" />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative bg-slate-950 text-white py-24 md:py-32 border-b border-slate-900">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/b4da8d98-d34d-46de-b1d1-17978c9253fd.jpg"
            alt="Ydelser"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-12">
          <span className="text-blue-500 text-xs font-extrabold tracking-[0.25em] uppercase block mb-3">
            Hvad vi tilbyder
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-wider uppercase">
            Vores Kompetencer
          </h1>
          <p className="mt-4 text-sm md:text-base text-gray-400 max-w-2xl leading-relaxed">
            Møldrup VVS A/S udfører alle former for VVS- og blikkenslagerarbejde. Vi garanterer høj faglig stolthed og veludført arbejde til aftalt tid.
          </p>
        </div>
      </section>

      {/* Competencies List Section */}
      <section className="py-20 bg-white text-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Intro */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-blue-600 text-xs font-extrabold tracking-[0.25em] uppercase block">
                Fuld dækning
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-wider uppercase">
                Bred vifte af VVS-løsninger
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                Vi har mange års erfaring og løser opgaver for både private boligejere, erhvervsvirksomheder, boligselskaber og offentlige institutioner.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                Vores medarbejdere er veluddannede og opdaterede med de nyeste teknologier inden for energioptimering, varmeanlæg og sanitet.
              </p>
              
              {/* Subpages Links */}
              <div className="pt-6 space-y-4">
                <Link
                  to="/ydelser/haandvaerkergaranti"
                  className="flex items-center justify-between p-4 bg-slate-50 border border-gray-100 hover:border-blue-500 transition-colors duration-200 rounded-none group"
                >
                  <div className="flex items-center space-x-3">
                    <ShieldCheck className="w-5 h-5 text-blue-600" />
                    <span className="text-xs font-bold tracking-wider uppercase">Håndværkergaranti</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors duration-150" />
                </Link>

                <Link
                  to="/ydelser/leverandoerer"
                  className="flex items-center justify-between p-4 bg-slate-50 border border-gray-100 hover:border-blue-500 transition-colors duration-200 rounded-none group"
                >
                  <div className="flex items-center space-x-3">
                    <Award className="w-5 h-5 text-blue-600" />
                    <span className="text-xs font-bold tracking-wider uppercase">Vores Leverandører</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors duration-150" />
                </Link>
              </div>
            </div>

            {/* Right Column: Verbatim List */}
            <div className="lg:col-span-7 bg-slate-50 p-8 md:p-12 border border-gray-100">
              <h3 className="text-sm font-extrabold uppercase tracking-widest mb-6 text-slate-800 border-b border-gray-200 pb-3">
                Arbejdsområder
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {competencies.map((comp, idx) => (
                  <div key={idx} className="flex items-center space-x-3 text-sm text-gray-700">
                    <span className="w-1.5 h-1.5 bg-blue-600 shrink-0" />
                    <span className="font-semibold">{comp}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Galleries Section */}
      {galleries.length > 0 && (
        <section className="py-20 bg-slate-50 text-slate-900 border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
              <span className="text-blue-600 text-xs font-extrabold tracking-[0.25em] uppercase block">
                Galleri & Referencer
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-wider uppercase">
                Udførte projekter i billeder
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                Se billeder af vores arbejde inden for badeværelser, varmeanlæg, erhvervsbyggeri og meget mere. Klik på et billede for stor visning.
              </p>
            </div>

            <div className="space-y-16">
              {galleries.map((gallery, gIdx) => (
                <div key={gallery.id || gIdx} className="space-y-6">
                  <div className="border-b border-gray-200 pb-3">
                    <h3 className="text-xl font-extrabold uppercase tracking-wider text-slate-800">
                      {gallery.title}
                    </h3>
                    {gallery.description && (
                      <p className="text-gray-500 text-xs mt-1">{gallery.description}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {(gallery.images || []).map((img, imgIdx) => (
                      <div
                        key={imgIdx}
                        onClick={() => openLightbox(gallery.images, imgIdx)}
                        className="relative aspect-video md:aspect-square overflow-hidden bg-slate-950 rounded-none cursor-pointer group"
                      >
                        <img
                          src={img}
                          alt={`${gallery.title} ${imgIdx + 1}`}
                          className="w-full h-full object-cover opacity-90 transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <GalleryLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
